import HasWriteAccess from '@app/src/components/HasWriteAccess'
import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import useLocalisedCountryName from '@app/src/hooks/useLocalisedCountryName'
import { useProviderEnrichedValues } from '@app/src/hooks/useProviderEnrichedValues'
import type { Provider } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import { ensureWebsiteClickability, normalizeWebsiteDisplay } from '@app/src/utils/helpers'
import { AutoAwesomeOutlined, LockOutlined } from '@mui/icons-material'
import type { SxProps, Theme } from '@mui/material'
import { Box, Button, Divider, Link, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import ReactCountryFlag from 'react-country-flag'
import { useIntl } from 'react-intl'

interface OrganizationInformationSectionProps {
  provider: Provider
}

const truncatedTextSx: SxProps<Theme> = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
}

const EmptyValue = () => <Typography color="text.secondary">-</Typography>

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" alignItems="flex-start">
    <Typography color="text.secondary" sx={{ width: '50%', flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end' }}>{children}</Box>
  </Stack>
)

const EnrichedLabel: React.FC<{ value?: string | null; isEnriched?: boolean }> = ({ value, isEnriched = false }) => {
  const { formatMessage } = useIntl()

  if (!value) {
    return <EmptyValue />
  }

  return (
    <Tooltip title={value}>
      <Typography textAlign="right" sx={truncatedTextSx}>
        {isEnriched && (
          <Tooltip title={formatMessage({ id: 'general.aiEnriched' })}>
            <AutoAwesomeOutlined
              sx={{
                fontSize: 16,
                color: 'communication.dark',
                verticalAlign: 'text-bottom',
                mr: 0.5,
              }}
            />
          </Tooltip>
        )}
        {value}
      </Typography>
    </Tooltip>
  )
}

const OrganizationInformationSection: React.FC<OrganizationInformationSectionProps> = ({ provider }) => {
  const { formatMessage } = useIntl()
  const { getLocalisedCountryName } = useLocalisedCountryName()
  const { handleOpenConnectDialog } = useProvidersModal()
  const enrichedValues = useProviderEnrichedValues(provider)

  const info = provider.organization || provider
  const isConnected = provider.linkStatus === ProviderLinkStatus.Connected

  const countryCode = provider.country?.countryCode
  const countryName = getLocalisedCountryName(countryCode)

  const websiteDisplay = enrichedValues.websiteAddress.value
    ? normalizeWebsiteDisplay(enrichedValues.websiteAddress.value)
    : undefined
  const websiteHref = enrichedValues.websiteAddress.value
    ? ensureWebsiteClickability(enrichedValues.websiteAddress.value)
    : undefined
  const safeWebsiteHref = websiteHref && /^https?:\/\//i.test(websiteHref) ? websiteHref : undefined

  const employeesValue = enrichedValues.companySizeRange.value
    ? formatMessage({ id: `claim.companySize.${enrichedValues.companySizeRange.value}` })
    : undefined

  const revenueValue = enrichedValues.revenueRange.value
    ? formatMessage({ id: `claim.companyRevenue.${enrichedValues.revenueRange.value}` })
    : undefined

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'general.orgInformation' })}
        </Typography>
      </Stack>

      <Stack spacing={3} sx={{ bgcolor: 'common.white', borderRadius: 2, px: 3, py: 4 }}>
        <InfoRow label={formatMessage({ id: 'schemas.organization.address' })}>
          <EnrichedLabel value={info.locations?.[0]?.address} />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.supplier.country' })}>
          {countryName ? (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
              {countryCode && (
                <ReactCountryFlag countryCode={countryCode} svg style={{ fontSize: '1.25em' }} title={countryName} />
              )}
              <Typography>{countryName}</Typography>
            </Stack>
          ) : (
            <EmptyValue />
          )}
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.organization.websiteAddress' })}>
          {websiteDisplay ? (
            <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
              {enrichedValues.websiteAddress.isEnriched && (
                <Tooltip title={formatMessage({ id: 'general.aiEnriched' })}>
                  <AutoAwesomeOutlined sx={{ fontSize: 16, color: 'communication.dark' }} />
                </Tooltip>
              )}
              <Link
                href={safeWebsiteHref}
                underline="hover"
                rel="noopener noreferrer"
                target="_blank"
                sx={truncatedTextSx}
              >
                {websiteDisplay}
              </Link>
            </Stack>
          ) : (
            <EmptyValue />
          )}
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.organization.industry' })}>
          <EnrichedLabel value={enrichedValues.industry.value} isEnriched={enrichedValues.industry.isEnriched} />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.organization.subIndustry' })}>
          <EnrichedLabel value={enrichedValues.subIndustry.value} isEnriched={enrichedValues.subIndustry.isEnriched} />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.organization.employees' })}>
          <EnrichedLabel value={employeesValue} isEnriched={enrichedValues.companySizeRange.isEnriched} />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.organization.revenue' })}>
          <EnrichedLabel value={revenueValue} isEnriched={enrichedValues.revenueRange.isEnriched} />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.supplier.vatNumber' })}>
          <EnrichedLabel value={info.vatNumber} isEnriched={enrichedValues.vatNumber.isEnriched} />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.supplier.registrationNumber' })}>
          <EnrichedLabel
            value={enrichedValues.registrationNumber.value}
            isEnriched={enrichedValues.registrationNumber.isEnriched}
          />
        </InfoRow>

        <InfoRow label={formatMessage({ id: 'schemas.organization.duns' })}>
          <EnrichedLabel value={provider.duns} />
        </InfoRow>
        <Stack spacing={3}>
          <Divider />

          <Box>
            {isConnected ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <LockOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {formatMessage({ id: 'general.orgManagedByConnectedOrg' })}
                </Typography>
              </Stack>
            ) : enrichedValues.latestEnrichment?.completedAt ? (
              <Typography variant="body2" color="text.secondary">
                {formatMessage(
                  { id: 'general.propertiesAutoFilledOn' },
                  {
                    date: new Date(enrichedValues.latestEnrichment.completedAt).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    }),
                  },
                )}
              </Typography>
            ) : (
              <HasWriteAccess>
                <Button
                  size="small"
                  startIcon={<AutoAwesomeOutlined />}
                  onClick={() => handleOpenConnectDialog([provider], ProvidersModalPurpose.ScheduleEnrichment)}
                  sx={{ color: 'text.secondary' }}
                >
                  {formatMessage({ id: 'general.autoFillProperties' })}
                </Button>
              </HasWriteAccess>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}

export default OrganizationInformationSection
